import React, { useEffect, useMemo, useRef } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import GlobalFormShell from "../../../../global/forms/GlobalFormShell";
import GlobalUserSearchField from "../../../../global/forms/GlobalUserSearchField";
import {
  GlobalTextField,
  GlobalContactNumberField,
  GlobalSelectField,
  GlobalDateField,
  GlobalTextAreaField,
  GlobalButton,
} from "../../../../global/forms/GlobalFormFields";
import { useVisitorContext } from "../../components/context/VisitorContext";
import { searchStaffUsers } from "../../api/visitorsApi";
import {
  buildVisitorFullSchema,
  buildVisitorDirectoryEditSchema,
  buildVisitorPayload,
  getDefaultVisitorFormValues,
  normalizeVisitorName,
} from "../../utils/visitorValidation";
import {
  normalizeIdProofNumber,
  resolveIdProofKind,
  idProofPlaceholder,
} from "../../utils/idProofValidation";
import {
  formatStaffDepartmentLabel,
  getTodayDateString,
  mapApiDetailsToFormErrors,
} from "../../utils/visitorHelpers";
import { handlePrintVisitorSlipStub } from "../../utils/visitorSlip";

const ALERT_ID = "visitor-form-alert";

const FIELD_FULL = { fieldWrap: "gff-field gfs-remarks-full" };

function formLegend(mode) {
  if (mode === "create") return "Capture visitor entry";
  if (mode === "edit") return "Edit visitor";
  return "Visitor details";
}

function purposeOptions(purposes) {
  return (purposes || []).map((p) => ({
    value: String(p.id),
    label: p.purpose_name,
  }));
}

function idProofOptions(types) {
  return (types || []).map((t) => ({
    value: String(t.id),
    label: t.type_name,
  }));
}

export default function VisitorForm({
  mode = "create",
  limitedEdit = false,
  initialValues,
  visitorCode = "",
  personToMeetName = "",
  personToMeetDepartment = "",
  approvedByName = "",
  approvedByDepartment = "",
  onSubmit,
  onCancel,
  submitting = false,
  serverError = "",
  showPrintSlipStub = false,
}) {
  const { purposes, idProofTypes, loadLookups, loadingLookups } = useVisitorContext();
  const isLimited = limitedEdit || mode === "limited";
  const idProofTypesRef = useRef(idProofTypes);
  idProofTypesRef.current = idProofTypes;

  const resolveIdProofTypeName = (typeId) =>
    idProofTypesRef.current.find((t) => String(t.id) === String(typeId))?.type_name ??
    null;

  const schema = useMemo(
    () =>
      isLimited
        ? buildVisitorDirectoryEditSchema({ getIdProofTypeName: resolveIdProofTypeName })
        : buildVisitorFullSchema({
            entryDateLocked: mode === "create",
            getIdProofTypeName: resolveIdProofTypeName,
          }),
    [isLimited, mode],
  );

  const {
    control,
    handleSubmit,
    setError,
    reset,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: initialValues || getDefaultVisitorFormValues(),
    mode: "onBlur",
  });

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    if (initialValues) reset(initialValues);
  }, [initialValues, reset]);

  const idProofTypeId = useWatch({ control, name: "id_proof_type_id" });
  const selectedIdProofType = (idProofTypes || []).find(
    (t) => String(t.id) === String(idProofTypeId ?? ""),
  );
  const idProofKind = resolveIdProofKind(selectedIdProofType?.type_name);
  const prevIdProofTypeRef = useRef(undefined);

  useEffect(() => {
    if (
      prevIdProofTypeRef.current !== undefined &&
      prevIdProofTypeRef.current !== idProofTypeId
    ) {
      setValue("id_proof_number", "", { shouldValidate: false });
    }
    prevIdProofTypeRef.current = idProofTypeId;
  }, [idProofTypeId, setValue]);

  const applyServerDetails = (details) => {
    const mapped = mapApiDetailsToFormErrors(details);
    Object.entries(mapped).forEach(([field, message]) => {
      setError(field, { type: "server", message });
    });
  };

  const submitHandler = async (values) => {
    try {
      const payload = buildVisitorPayload(values, { limited: isLimited, idProofTypes });
      await onSubmit?.(payload);
    } catch (err) {
      if (err?.details && Object.keys(err.details).length > 0) {
        applyServerDetails(err.details);
      }
    }
  };

  const today = getTodayDateString();

  return (
    <form onSubmit={handleSubmit(submitHandler)} noValidate>
    <GlobalFormShell
      legend={formLegend(mode)}
      alertId={ALERT_ID}
      showAlert={Boolean(serverError)}
      alertText={serverError}
      overflowVisible
      gridClassName="gfs-form-grid--register"
      actions={
        <>
          {onCancel ? (
            <GlobalButton type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
              Cancel
            </GlobalButton>
          ) : null}
          {showPrintSlipStub ? (
            <GlobalButton
              type="button"
              variant="ghost"
              disabled={submitting}
              onClick={() => {
                // TODO: Generate visitor slip PDF and print after saving visitor.
                handlePrintVisitorSlipStub();
              }}
            >
              Print Visitor Slip
            </GlobalButton>
          ) : null}
          <GlobalButton type="submit" variant="primary" disabled={submitting || loadingLookups}>
            {submitting
              ? "Saving…"
              : mode === "create"
                ? "Save Visitor Entry"
                : "Save changes"}
          </GlobalButton>
        </>
      }
    >
      <Controller
        name="entry_date"
        control={control}
        render={({ field }) => (
          <GlobalDateField
            id="entry_date"
            label="Entry Date"
            required
            value={field.value}
            onChange={field.onChange}
            min={isLimited ? undefined : today}
            max={isLimited ? undefined : today}
            disabled={mode === "create" && !isLimited}
            error={errors.entry_date?.message}
            baseAlertId={ALERT_ID}
          />
        )}
      />

      <Controller
        name="in_time"
        control={control}
        render={({ field }) => (
          <GlobalTextField
            id="in_time"
            label="In Time"
            type="time"
            required
            value={field.value}
            onChange={field.onChange}
            error={errors.in_time?.message}
            baseAlertId={ALERT_ID}
          />
        )}
      />

      <Controller
        name="out_time"
        control={control}
        render={({ field }) => (
          <GlobalTextField
            id="out_time"
            label="Out Time"
            type="time"
            value={field.value}
            onChange={field.onChange}
            error={errors.out_time?.message}
            baseAlertId={ALERT_ID}
          />
        )}
      />

      {visitorCode ? (
        <GlobalTextField
          id="visitor_code_display"
          label="Visitor Code"
          value={visitorCode}
          onChange={() => {}}
          disabled
          baseAlertId={ALERT_ID}
        />
      ) : null}

      <Controller
        name="visitor_name"
        control={control}
        render={({ field }) => (
          <GlobalTextField
            id="visitor_name"
            label="Visitor Name"
            required
            value={field.value}
            onChange={(value) => field.onChange(normalizeVisitorName(value))}
            maxLength={30}
            error={errors.visitor_name?.message}
            placeholder="Enter visitor name"
            baseAlertId={ALERT_ID}
          />
        )}
      />

      <Controller
        name="mobile_number"
        control={control}
        render={({ field }) => (
          <GlobalContactNumberField
            id="mobile_number"
            label="Mobile Number"
            required
            value={field.value}
            onChange={field.onChange}
            error={errors.mobile_number?.message}
            placeholder="10-digit phone number"
            baseAlertId={ALERT_ID}
          />
        )}
      />

      <Controller
        name="purpose_id"
        control={control}
        render={({ field }) => (
          <GlobalSelectField
            id="purpose_id"
            label="Purpose of Visit"
            required
            value={field.value}
            onChange={field.onChange}
            options={purposeOptions(purposes)}
            placeholderOption="Select purpose"
            error={errors.purpose_id?.message}
            disabled={loadingLookups}
            baseAlertId={ALERT_ID}
          />
        )}
      />

      <Controller
        name="person_to_meet"
        control={control}
        render={({ field }) => (
          <GlobalUserSearchField
            label="Person To Meet"
            required
            value={field.value}
            onChange={field.onChange}
            searchUsers={searchStaffUsers}
            error={errors.person_to_meet?.message}
            initialDisplayName={personToMeetName}
            initialDisplayDepartment={formatStaffDepartmentLabel({
              department_name: personToMeetDepartment,
            })}
            wrapClassName="gus-wrap--form-span"
            placeholder="Type name (min 2 letters)…"
            helperText="Search staff by name, then click a result or press Enter."
          />
        )}
      />

      {!isLimited ? (
        <Controller
          name="address"
          control={control}
          render={({ field }) => (
            <GlobalTextAreaField
              id="address"
              label="Address / Place"
              value={field.value}
              onChange={field.onChange}
              error={errors.address?.message}
              classNames={FIELD_FULL}
              rows={2}
              baseAlertId={ALERT_ID}
            />
          )}
        />
      ) : null}

      <Controller
        name="id_proof_type_id"
        control={control}
        render={({ field }) => (
          <GlobalSelectField
            id="id_proof_type_id"
            label="ID Proof Type"
            value={field.value}
            onChange={(value) => {
              field.onChange(value);
              void trigger("id_proof_number");
            }}
            options={idProofOptions(idProofTypes)}
            placeholderOption="Optional"
            error={errors.id_proof_type_id?.message}
            disabled={loadingLookups}
            baseAlertId={ALERT_ID}
          />
        )}
      />

      <Controller
        name="id_proof_number"
        control={control}
        render={({ field }) => (
          <GlobalTextField
            id="id_proof_number"
            label="ID Proof Number"
            value={field.value}
            onChange={(value) =>
              field.onChange(
                idProofKind
                  ? normalizeIdProofNumber(idProofKind, value)
                  : String(value ?? "").trim(),
              )
            }
            onBlur={field.onBlur}
            disabled={!idProofTypeId}
            error={errors.id_proof_number?.message}
            placeholder={idProofPlaceholder(selectedIdProofType?.type_name)}
            baseAlertId={ALERT_ID}
          />
        )}
      />

      {!isLimited ? (
        <>
          <Controller
            name="visitor_pass_issued"
            control={control}
            render={({ field }) => (
              <p className="gff-field">
                <label className="gff-label" style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={Boolean(field.value)}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                  Visitor Pass Issued
                </label>
              </p>
            )}
          />

          <Controller
            name="approved_by"
            control={control}
            render={({ field }) => (
              <GlobalUserSearchField
                label="Entry Approved By"
                value={field.value}
                onChange={field.onChange}
                searchUsers={searchStaffUsers}
                error={errors.approved_by?.message}
                initialDisplayName={approvedByName}
                initialDisplayDepartment={formatStaffDepartmentLabel({
                  department_name: approvedByDepartment,
                })}
                wrapClassName="gus-wrap--form-span"
                placeholder="Type name (min 2 letters)…"
                helperText="Optional. Search and select a staff member from the list."
              />
            )}
          />

          <Controller
            name="remarks"
            control={control}
            render={({ field }) => (
              <GlobalTextAreaField
                id="remarks"
                label="Remarks"
                value={field.value}
                onChange={field.onChange}
                error={errors.remarks?.message}
                classNames={FIELD_FULL}
                rows={3}
                baseAlertId={ALERT_ID}
              />
            )}
          />
        </>
      ) : null}
    </GlobalFormShell>
    </form>
  );
}
